"use client";

import { type ReactNode, useCallback, useMemo, useState } from "react";
import {
  HydratedAsset,
  useSplitTreatments,
  useBulkCheckMovePermissions,
} from "@zapier/client";
import { AssetTypes } from "@zapier/utils";
import { useAssetListBulkActions } from "../../../hooks/useAssetListBulkActions";
import type { BulkSelectAction } from "../../common/Table/BulkActions/BulkActions";
import { BulkActionsBarWrapper } from "../../common/Table/BulkActions/BulkActionsBarWrapper";
import { BulkMoveModal } from "./BulkMoveModal";
import { CanvasBulkChangeOwnerModal } from "./CanvasBulkChangeOwnerModal";
import { CopyToAnotherAccountModal } from "../../zsl/CopyToAnotherAccountModal/CopyToAnotherAccountModal";
import { AssetDeleteModal } from "../AssetDeleteModal/AssetDeleteModal";
import {
  CopyCandidateAsset,
  resolveSelectedCopyAssets,
  toHydratedAsset,
} from "./bulkCopySelection";

interface BulkActionsWrapperProps {
  assetType?: AssetTypes;
  folderId?: string;
  assetsForCopyToAnotherAccount?: CopyCandidateAsset[];
  // Raw-id -> display name, used to name a single-asset move-to-trash modal.
  assetNamesById?: Record<string, string>;
  children: (props: {
    bulkSelectActions: BulkSelectAction[] | undefined;
    onCopyToAnotherAccount?: (asset: CopyCandidateAsset) => void;
  }) => ReactNode;
}

/**
 * Encapsulates the bulk-actions feature-flag check, action hook, floating bar,
 * and modal so each list page doesn't have to wire them up individually.
 *
 * Named generically because it will grow to support bulk delete, share, etc.
 *
 * Usage:
 * ```
 * <BulkActionsWrapper assetType={AssetTypes.Canvas}>
 *   {({ bulkSelectActions }) => (
 *     <CanvasesTable bulkSelectActions={bulkSelectActions} ... />
 *   )}
 * </BulkActionsWrapper>
 * ```
 */
export function BulkActionsWrapper({
  assetType,
  folderId,
  assetsForCopyToAnotherAccount,
  assetNamesById,
  children,
}: BulkActionsWrapperProps) {
  const {
    isBulkMoveAssetsEnabled,
    isDuplicateAssetsEnabled,
    isZapAndCanvasUnifiedTrashEnabled,
    isBulkChangeOwnerEnabled: isBulkChangeOwnerFlagOn,
    isNgzBulkMoveEnabled,
    isNgzBulkTrashEnabled,
    isNgzBulkChangeOwnerEnabled,
    isNgzBulkCopyEnabled,
  } = useSplitTreatments();
  const isNextGenZap = assetType === AssetTypes.NextGenZap;

  const { check, ...permissionResult } = useBulkCheckMovePermissions();
  const [rowCopyAssets, setRowCopyAssets] = useState<HydratedAsset[]>([]);
  const hasCopyCandidates = useMemo(
    () =>
      assetsForCopyToAnotherAccount?.some(
        (asset) => typeof asset.id === "string" && asset.id.length > 0,
      ) ?? false,
    [assetsForCopyToAnotherAccount],
  );

  const handleMoveClicked = useCallback(
    (typedIds: string[]) => {
      check(typedIds);
    },
    [check],
  );

  const handleBulkCopyClicked = useCallback(() => {
    setRowCopyAssets([]);
  }, []);

  // Next Gen Zaps gate each bulk action on its own flag; other types use the shared flags.
  const isBulkCopyEnabled =
    (isNextGenZap ? isNgzBulkCopyEnabled : isDuplicateAssetsEnabled) &&
    hasCopyCandidates &&
    !!assetType;

  const isBulkDeleteEnabled = isNextGenZap
    ? isNgzBulkTrashEnabled
    : isZapAndCanvasUnifiedTrashEnabled && assetType === AssetTypes.Canvas;

  const isBulkChangeOwnerEnabled = isNextGenZap
    ? isNgzBulkChangeOwnerEnabled
    : isBulkChangeOwnerFlagOn && assetType === AssetTypes.Canvas;

  // `!!` is load-bearing for tests, not real traffic: useSplitTreatments
  // always resolves isNgzBulkMoveEnabled to a strict boolean, but a test mock
  // that omits it leaves it undefined, and useAssetListBulkActions defaults
  // isBulkMoveEnabled to true when it's undefined - without the coercion,
  // that mock would quietly turn Move on.
  const isBulkMoveEnabledForAssetType = isNextGenZap
    ? !!isNgzBulkMoveEnabled
    : isBulkMoveAssetsEnabled;

  const {
    bulkSelectActions,
    isMoveModalOpen,
    setMoveModalOpen,
    selectedTypedAssetIds,
    onMoveSettled,
    isCopyModalOpen,
    setCopyModalOpen,
    selectedTypedAssetIdsForCopy,
    isDeleteModalOpen,
    setDeleteModalOpen,
    onDeleteSettled,
    isChangeOwnerModalOpen,
    setChangeOwnerModalOpen,
    onChangeOwnerSettled,
  } = useAssetListBulkActions({
    assetType,
    onMoveClicked: handleMoveClicked,
    onCopyToAnotherAccountClicked: handleBulkCopyClicked,
    isBulkMoveEnabled: isBulkMoveEnabledForAssetType,
    isBulkCopyEnabled,
    isBulkDeleteEnabled,
    isBulkChangeOwnerEnabled,
  });

  const bulkCopyAssets = useMemo(() => {
    return resolveSelectedCopyAssets({
      assetType,
      assetsForCopyToAnotherAccount,
      selectedTypedAssetIdsForCopy,
    });
  }, [assetType, assetsForCopyToAnotherAccount, selectedTypedAssetIdsForCopy]);

  // Same per-type gate as isBulkCopyEnabled above: Next Gen Zaps read their
  // own flag, everything else reads the shared one. Keeping both checks on
  // this one ternary is what stops NGZ copy from ending up gated behind two
  // different flags once a real onCopyToAnotherAccount lands for it.
  const isRowCopyEnabledForAssetType = isNextGenZap
    ? isNgzBulkCopyEnabled
    : isDuplicateAssetsEnabled;

  const handleCopyToAnotherAccount = useCallback(
    (asset: CopyCandidateAsset) => {
      if (!isRowCopyEnabledForAssetType) {
        return;
      }

      const hydratedAsset = toHydratedAsset(asset, assetType);
      if (!hydratedAsset) {
        return;
      }

      setRowCopyAssets([hydratedAsset]);
      setCopyModalOpen(true);
    },
    [assetType, isRowCopyEnabledForAssetType, setCopyModalOpen],
  );

  const copyModalAssets =
    rowCopyAssets.length > 0 ? rowCopyAssets : bulkCopyAssets;
  const copyEntryPoint =
    rowCopyAssets.length > 0 ? "row_kebab" : "bulk_selection";
  const shouldShowCopyModal = isCopyModalOpen && copyModalAssets.length > 0;

  const activeBulkSelectActions =
    bulkSelectActions.length > 0 ? bulkSelectActions : undefined;

  const singleDeleteAssetName = useMemo(() => {
    if (selectedTypedAssetIds.length !== 1 || !assetNamesById) {
      return undefined;
    }
    const [typedId] = selectedTypedAssetIds;
    const separatorIndex = typedId.indexOf(":");
    const rawId =
      separatorIndex === -1 ? typedId : typedId.slice(separatorIndex + 1);
    return assetNamesById[rawId] ?? assetNamesById[typedId];
  }, [assetNamesById, selectedTypedAssetIds]);

  return (
    <>
      {children({
        bulkSelectActions: activeBulkSelectActions,
        onCopyToAnotherAccount: isRowCopyEnabledForAssetType
          ? handleCopyToAnotherAccount
          : undefined,
      })}
      <BulkActionsBarWrapper />
      {isBulkMoveEnabledForAssetType && (
        <BulkMoveModal
          selectedAssetIds={selectedTypedAssetIds}
          isOpen={isMoveModalOpen}
          setIsOpen={setMoveModalOpen}
          onSettled={onMoveSettled}
          folderId={folderId}
          permissionResult={permissionResult}
        />
      )}
      {shouldShowCopyModal && (
        <CopyToAnotherAccountModal
          isOpen={isCopyModalOpen}
          setIsOpen={(isOpen) => {
            if (isOpen) {
              setCopyModalOpen(true);
              return;
            }

            setCopyModalOpen(false);
            setRowCopyAssets([]);
          }}
          assets={copyModalAssets}
          copyEntryPoint={copyEntryPoint}
        />
      )}
      {isBulkDeleteEnabled && (
        <AssetDeleteModal
          selectedAssetIds={selectedTypedAssetIds}
          isOpen={isDeleteModalOpen}
          setIsOpen={setDeleteModalOpen}
          onSettled={onDeleteSettled}
          singleAssetName={singleDeleteAssetName}
        />
      )}
      {isBulkChangeOwnerEnabled && !isNextGenZap && (
        <CanvasBulkChangeOwnerModal
          selectedAssetIds={selectedTypedAssetIds}
          isOpen={isChangeOwnerModalOpen}
          setIsOpen={setChangeOwnerModalOpen}
          onSettled={onChangeOwnerSettled}
          folderId={folderId}
        />
      )}
    </>
  );
}
